import { format, formatDistanceToNow } from 'date-fns'
import { get } from 'lodash'
import publicIp from 'public-ip'

export const formatDate = (timestamp, formatString = 'ddd, Ha') => format(new Date(timestamp), formatString)
export const relativeDate = timestamp => formatDistanceToNow(new Date(timestamp))
export const formatCurrency = amount => {
  const num = Number(amount)
  const value = Number.isNaN(num) ? 0 : num
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
}
export const formatCamperType = type => {
  switch (type) {
    case 'WL':
      return 'WL (Jr. High)'
    case 'SH':
      return 'SH (Sr. High)'
    case 'EL&SR':
      return 'WL&SR (Jr. and Sr. High)'
    case 'Leader':
      return 'Leader'
    default:
      return type
  }
}
export const isCreditCardPayment = payment => payment.attributes['payment-type'] === 'Credit Card'

export const jsonapiToFormErrors = (values, response, setStatus) => {
  // {"errors":[{"source":{"pointer":"/data/attributes/password-confirmation"},"detail":"doesn't match Password"}]}
  let formErrors = {}
  // Several errors can land on the same initial box, so join them rather than keeping only the last one.
  const addFormError = (formKey, message) => {
    const existing = formErrors[formKey] ? formErrors[formKey].split('; ') : []
    if (!existing.includes(message)) formErrors[formKey] = existing.concat(message).join('; ')
  }

  const errors = get(response, 'body.errors', [])
  if (errors.length === 0) {
    console.log('Invalid error response!', response)
    return {}
  }

  errors.forEach(error => {
    const key =
      error.source.pointer
        .replace(/\/data\/attributes\//, '')
        .replace(/-/g, '_')
        .toLowerCase() || 'base'
    switch (key) {
      case 'province_health_insurance_policy_num':
      case 'province_health_insurance_policy_province':
      case 'foreign_medical_co_contact_number':
      case 'foreign_medical_co_emergency_phone_number':
      case 'foreign_medical_co_name':
      case 'foreign_medical_co_policy_number':
        if (values.health_insurance_choice.length === 0) {
          formErrors['health_insurance_choice'] = 'missing fields'
        }
        formErrors[key] = error.detail
        break
      case 'initials': {
        // Pull the initial type from the message
        const match = error.detail.match(/for ([a-z_]+)(.*)/)
        if (match) {
          addFormError(`initials.${match[1]}`, match[2].trim())
        } else {
          formErrors[key] = error.detail
        }
        break
      }
      case 'initials.comment':
      case 'initials.email':
      case 'initials.ip_address': {
        // Nested initial errors don't say which initial they belong to, so work it out from the values that were
        // sent: promotional photos for comments (the only initial that has one), otherwise the initials missing that
        // attribute.
        const attribute = key.replace('initials.', '')
        const typeMatch = error.detail.match(/for ([a-z_]+)\s*(.*)/)
        const message = typeMatch ? typeMatch[2] : error.detail
        const sentInitials = get(values, 'initials_attributes') || {}
        let initialTypes = []
        if (typeMatch) {
          initialTypes = [typeMatch[1]]
        } else if (attribute === 'comment') {
          initialTypes = ['promotional_photos']
        } else {
          initialTypes = Object.keys(sentInitials).filter(
            initialType => sentInitials[initialType] && !sentInitials[initialType][attribute]
          )
        }
        if (initialTypes.length === 0) {
          formErrors[key] = error.detail
          break
        }

        initialTypes.forEach(initialType => {
          if (attribute === 'comment') {
            // Keyed by the comment field's name so the error shows under the comment box.
            formErrors[`initials_attributes.${initialType}.comment`] = message
            return
          }
          addFormError(`initials.${initialType}`, `${attribute.replace('_', ' ')} ${message}`)
        })
        break
      }
      default:
        formErrors[key] = error.detail
        break
    }
  })
  setStatus({ apiErrors: formErrors })

  return formErrors
}

export const dashToUnderscoreObject = dasherizedObject => {
  let underscorizedObject = {}
  Object.keys(dasherizedObject).forEach(key => {
    underscorizedObject[key.replace(/-/g, '_')] = dasherizedObject[key]
  })
  return underscorizedObject
}

export const urlFor = (route, params = {}, querystringParams = {}) => {
  let url = route
  Object.keys(params).forEach(key => {
    url = url.replace(`:${key}`, params[key])
  })

  const querystring = Object.keys(querystringParams)
    .map(key => `${key}=${querystringParams[key]}`)
    .join('&')
  if (querystring && querystring.length > 0) {
    url += `?${querystring}`
  }

  return url
}

export const getGendersForSelect = () => [
  {
    label: 'M',
    value: 'M',
  },
  {
    label: 'F',
    value: 'F',
  },
  {
    label: 'Other',
    value: 'Other',
  },
]

export const getMedicalConditionsForForm = () => [
  'ADHD (Hyperactive Disorder)',
  'Autism spectrum disorder',
  'Behaviour issues',
  'Bedwetting',
  'Bleeding condition',
  'Bone/joint condition',
  'Breathing Issues',
  'Cancer',
  'Diabetes',
  'Digestive condition',
  'Fainting',
  'Head Injury',
  'Heart condition',
  'Infections',
  'Intellectual developmental delays',
  'Mental health concerns',
  'Mobility challenges',
  'Operations or serious injury',
  'Seizures',
  'Other conditions not listed'
]

export const getImmunizationsForForm = () => [
  'DPT (Diphtheria/Pertussis/Tetanus)',
  'Polio',
  'MMR (Measles/Mumps/Rubella)',
  'Hep A',
  'Hep B',
  'Influenza',
]

export const getDietOptionsForForm = () => [
  'Vegetarian',
  'Vegan',
  'Celiac',
  'Gluten',
  'Dairy',
  'Soy',
  'Egg',
  'Nuts',
  'Sulfites',
  'Shellfish',
  'Seafood',
  'Vegetables',
  'Fruit',
  'Seeds',
  'Other'
]
export const getProvincesForSelect = () => [
  {
    label: 'AB',
    value: 'AB',
  },
  {
    label: 'BC',
    value: 'BC',
  },
  {
    label: 'MB',
    value: 'MB',
  },
  {
    label: 'NB',
    value: 'NB',
  },
  {
    label: 'NL',
    value: 'NL',
  },
  {
    label: 'NS',
    value: 'NS',
  },
  {
    label: 'NT',
    value: 'NT',
  },
  {
    label: 'NU',
    value: 'NU',
  },
  {
    label: 'ON',
    value: 'ON',
  },
  {
    label: 'PE',
    value: 'PE',
  },
  {
    label: 'QC',
    value: 'QC',
  },
  {
    label: 'SK',
    value: 'SK',
  },
]

export const getShirtSizesForSelect = () => [
  {
    label: 'XS',
    value: 'XS',
  },
  {
    label: 'S',
    value: 'S',
  },
  {
    label: 'M',
    value: 'M',
  },
  {
    label: 'L',
    value: 'L',
  },
  {
    label: 'XL',
    value: 'XL',
  },
]

export const getCreditCardOptionsForSelect = () => [
  {
    label: 'VISA',
    value: 'VISA',
  },
  {
    label: 'MC',
    value: 'MC',
  },
  {
    label: 'AMX',
    value: 'AMX',
  },
  {
    label: 'DSC',
    value: 'DSC',
  },
]
export const isPaid = (paidAmount, totalAmount) => parseFloat(paidAmount) >= parseFloat(totalAmount)
export const toCurrency = amount => `$${amount}`
export const titleize = string => string.replace(/^\w/, c => c.toUpperCase())

export const isLeaders = process.env.REACT_APP_LEADERS === 'true'

// Shared by concurrent callers so a slow lookup only runs once. Failures aren't cached, so the next call retries.
let publicIpRequest = null

export const getPublicIp = async () => {
  if (sessionStorage.getItem('publicIp')) {
    return sessionStorage.getItem('publicIp')
  }

  if (!publicIpRequest) {
    publicIpRequest = publicIp
      .v4()
      .then(ipAddress => {
        sessionStorage.setItem('publicIp', ipAddress)
        return ipAddress
      })
      .finally(() => {
        publicIpRequest = null
      })
  }
  return publicIpRequest
}
